<template>
	<DialogPanel
		class="topic-browser-panel"
		:scroll-body="false"
		@close="$emit( `close` )"
	>
		<template #title-text>{{ translate( "title" ) }}</template>

		<template #sub-header v-if="!showCategoryList">
			<div class="selector-header">
				<LabeledDropDown
					dropDownClass="category-dropdown"
					:disabled="disabled"
					:label="translate( `categoryLabel` )"
					:options="categoryOptions"
					:value="selectedTopicCategory.category"
					@change="$emit( `categoryChanged`, $event.value )"
				/>
			</div>
		</template>

		<template #body>
			<div class="sub-body">
				<div v-if="showCategoryList" class="sub-body--left">
					<TeamingChip2 v-for="c in categoryOptions"
						:key="c.value"
						class="topic-category"
						size="SMALL"
						:selected="c.value === selectedTopicCategory.category"
						@click="$emit( `categoryChanged`, c.value )"
					>
						{{ c.value }}
					</TeamingChip2>
				</div>
				<div class="sub-body--right">
					<div class="topics">
						<p class="description">{{ selectedTopicCategory.description }}</p>
						<div class="topic" v-for="topic in selectedTopicCategory.topics" :key="topic.id">
							<div class="topic--left">{{ topic.text }}</div>
							<div class="topic--right">
								<TeamingButton
									class="add-topic-button"
									type="SECONDARY"
									size="TINY"
									@click="$emit( `topicSelected`, topic )"
								>
									<span v-html="translate( `addTopicButton` )"></span>
								</TeamingButton>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</DialogPanel>
</template>

<script>
import {
	Enum
} from "@teaming/common-enums";

import {
	LabeledDropDown,
	DialogPanel,
	TeamingButton,
	TeamingChip2
} from "@teaming/common-ui";

export const DISPLAY_MODE = Enum.create( {
	COMPACT: "COMPACT",
	NORMAL: "NORMAL",
} );

export default {
	name: "TopicBrowserPanel",
	emits: [ "close", "categoryChanged", "topicSelected", ],
	components: {
		LabeledDropDown,
		TeamingButton,
		TeamingChip2,
		DialogPanel,
	},
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		mode: {
			type: String,
			default: "NORMAL",
			validator( value ) {
				return DISPLAY_MODE.includes( value );
			},
		},
		/** [ { category:String }, ... ] */
		topicCategories: {
			type: Array,
			required: true,
		},
		/**
		 * Topic := { id:String, text:String }
		 * { category:String, description:String, topics:[ Topic, ... ] }
		 */
		selectedTopicCategory: {
			type: Object,
			required: true,
		},
	},
	computed: {
		/** [ { text:String, value:String }, ... ] */
		categoryOptions() {
			return this.topicCategories.map( topicCategory => {
				const { category, } = topicCategory;
				return {
					text: category,
					value: category,
				};
			} );
		},
		showCategoryList() {
			return this.mode === DISPLAY_MODE.NORMAL;
		},
	},
	methods: {
		translate( key, args = {} ) {
			const { name, } = this.$options;
			return this.$t( `common-team.components.${ name }.${ key }`, args );
		},
	},
};
</script>

<style lang="scss" scoped>
$selectorHeaderBackgroundColor: $white;
$subBodyBackgroundColor--left: $white;
$descriptionColor: $color1-700;
$topicCategoryColor: $color1-500;
$topicBackgroundColor: $white;

.topic-browser-panel {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;

	position: absolute;
	top: 0;
	bottom: 0;
	width: 100%;
}

.topic-browser-panel .sub-body {
	flex: 1;
	display: flex;
	flex-direction: row;
	justify-content: flex-start;

	padding: 0;

	overflow-y: hidden;
	height: 100%;

	text-align: left;
}

.topic-browser-panel .sub-body--left {
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	@include auto-y-scroll();

	border-right: 0.0625rem solid $borderColor--dark;
	padding-top: 1rem;
	padding-right: 1rem;
	padding-left: 1.5rem;

	background-color: $subBodyBackgroundColor--left;
}

.topic-browser-panel .sub-body--right {
	flex: 5;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 2rem;

	text-align: left;

	padding-top: 1rem;
}

.topic-browser-panel .selector-header {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 2rem;

	padding: 1rem;

	background-color: $selectorHeaderBackgroundColor;
}

.topic-browser-panel .description {
	@include font-and-line-for(0);
	font-weight: 450;
	color: $descriptionColor;

	cursor: default;

	padding-bottom: 1rem;
}

.topic-browser-panel .topics {
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 0.5rem;

	@include auto-y-scroll();

	@include font-and-line-for(0);
	font-weight: 450;

	padding: 0 1rem;
}

.topic-browser-panel .topic {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	gap: 0.5rem;

	cursor: default;

	background-color: $topicBackgroundColor;

	border-radius: 0.5rem;
	border: 0.0625rem solid transparent;

	padding: 0.5rem;

	&:last-of-type {
		margin-bottom: 1rem;
	}

	&:hover {
		.add-topic-button {
			visibility: visible;
		}
	}
}

.topic-browser-panel .topic--left {
	flex: 1;
}

.topic-browser-panel .topic--right {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-end;
}

.topic-browser-panel .topic-category {
	border: none;

	&.teaming-chip2--default {
		color: $topicCategoryColor;
	}

	&:last-of-type {
		margin-bottom: 1rem;
	}

	&:only-child {
		margin-bottom: unset;
	}
}

.topic-browser-panel .add-topic-button {
	visibility: visible;

	&:hover {
		visibility: visible;
	}

	@include for-size('md') {
		visibility: hidden;
	}
}
</style>

<style lang="scss">
$bodyBackgroundColor: $color1-050;

.topic-browser-panel .body {
	background-color: $bodyBackgroundColor;
}

.topic-browser-panel .category-dropdown .teaming-drop-down__options {
	height: 18rem;
	@include auto-y-scroll();
}
</style>
